import { Box } from "@mui/material";
import { palette } from "@powerdiary/components-library/lib";
import { BookingType, IProviderData, IGroupData } from "types/appointmentBooking";
import { ISlot } from "../../AvailabilityShared.helpers";
import { IdNameItemModel, ProviderModel } from "types/generated/models";
import TimeSlotButton from "../TimeSlotButton";

import styles from "./TimeSlotSection.module.css";

interface ITimeSlotSectionProps {
    morningSlots: ISlot[] | null;
    afternoonSlots: ISlot[] | null;
    eveningSlots: ISlot[] | null;
    selectedSlot: ISlot | null;
    onSlotSelection: (slot: ISlot, selectedProvider?: IdNameItemModel) => void;
    provider: IProviderData;
    availableProviders: ProviderModel[];
    bookingType: BookingType;
    defaultStripeAccount: string;
    onProviderSelect: (providerData: IProviderData) => void;
    onGroupSelect: (groupData: IGroupData) => void;
}

export default function TimeSlotSection({
    morningSlots,
    afternoonSlots,
    eveningSlots,
    selectedSlot,
    onSlotSelection,
    provider,
    availableProviders,
    bookingType,
    defaultStripeAccount,
    onProviderSelect,
    onGroupSelect,
}: Readonly<ITimeSlotSectionProps>) {
    const renderSlots = (
        slots: ISlot[],
        backgroundColor: string,
        textColor: string,
        timeOfDay: "morning" | "afternoon" | "evening"
    ) => {
        if (!slots || slots.length === 0) return null;

        return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {slots.map((slot) => {
                    const isSelected = selectedSlot?.id === slot.id;
                    return (
                        <TimeSlotButton
                            key={slot.id}
                            slot={slot}
                            isSelected={isSelected}
                            backgroundColor={backgroundColor}
                            textColor={textColor}
                            timeOfDay={timeOfDay}
                            onClick={onSlotSelection}
                            provider={provider}
                            availableProviders={availableProviders}
                            bookingType={bookingType}
                            defaultStripeAccount={defaultStripeAccount}
                            onProviderSelect={onProviderSelect}
                            onGroupSelect={onGroupSelect}
                        />
                    );
                })}
            </Box>
        );
    };

    if (!morningSlots && !afternoonSlots && !eveningSlots) return null;

    return (
        <Box
            className={styles.wrapper}
            sx={(theme) => ({
                border: `1px solid ${palette.neutral["300"]}`,
                padding: 2,
                [theme.breakpoints.down("sm")]: {
                    margin: "4px",
                },
            })}
            data-test-id="section_time_slot"
        >
            {morningSlots && morningSlots.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    {renderSlots(morningSlots, palette.dusk["200"], palette.neutral["900"], "morning")}
                </Box>
            )}

            {afternoonSlots && afternoonSlots.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    {renderSlots(afternoonSlots, palette.sunflower["300"], palette.neutral["900"], "afternoon")}
                </Box>
            )}

            {eveningSlots && eveningSlots.length > 0 && (
                <Box>{renderSlots(eveningSlots, palette.twilight["100"], palette.neutral["900"], "evening")}</Box>
            )}
        </Box>
    );
}
